import React, { useState, useMemo, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import {
  CommonInput,
  CommonSelect,
  CommonDatePicker,
  CommonButton,
  CommonModal,
} from "../Input";
import Swal from "sweetalert2";
import countries from "../../../data/countries";

const OrganizationCustomerModal = ({ show, onHide, customer }) => {
  const isEdit = !!customer;

  const [beneficiaries, setBeneficiaries] = useState([
    { id: 1, isCollapsed: false, fullName: "", ownership: "" },
  ]);

  const options = {
    nationality: countries?.map((country) => ({
      value: country.name,
      label: country.name,
      flag: country.flag,
    })),
    idType: [
      { value: "nric", label: "NRIC" },
      { value: "passport", label: "Passport" },
    ],
    entityType: [
      { value: "private", label: "Private Limited" },
      { value: "sole", label: "Sole Proprietorship" },
      { value: "other", label: "Others (please specify)" },
    ],
    country: countries?.map((country) => ({
      value: country.name,
      label: country.name,
      flag: country.flag,
    })),
    phonePrefix: countries?.map((country) => ({
      value: country.dial,
      label: `${country.dial} (${country.code})`,
      flag: country.flag,
    })),
  };

  const [formData, setFormData] = useState({
    mainContactName: "",
    mainContactDob: "",
    mainContactEmail: "",
    mainContactNationality: null,
    mainContactIdType: null,
    mainContactIdNumber: "",
    mainContactJobTitle: "",
    residentialAddress: "",
    registeredName: "",
    registrationNumber: "",
    entityType: null,
    entityTypeOther: "",
    incorporationDate: "",
    businessActivity: "",
    phonePrefix: options.phonePrefix.find((p) => p.value === "+65") || null,
    phone: "",
    primaryEmail: "",
    localBranches: "",
    overseasBranches: "",
    overseasCountries: "",
    country: null,
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
  });

  useEffect(() => {
    if (show) {
      if (customer) {
        // Map customer data
        const phonePrefixValue = customer.phone?.split(" ")[0] || "+65";
        const phoneValue = customer.phone?.split(" ").slice(1).join(" ") || "";

        setFormData({
          mainContactName: "test manual",
          mainContactDob: "01/01/1990",
          mainContactEmail: customer.email || "",
          mainContactNationality: options.nationality.find(
            (o) => o.value === "Singapore",
          ),
          mainContactIdType: options.idType.find((o) => o.value === "nric"),
          mainContactIdNumber: customer.idCard || "",
          mainContactJobTitle: "Director",
          residentialAddress: "123 Main St",
          registeredName: customer.name || "",
          registrationNumber: "REG123456",
          entityType: options.entityType.find((o) => o.value === "private"),
          entityTypeOther: "",
          incorporationDate: "01/01/2010",
          businessActivity: "IT Solutions",
          phonePrefix:
            options.phonePrefix.find((p) => p.value === phonePrefixValue) ||
            options.phonePrefix.find((p) => p.value === "+65"),
          phone: phoneValue,
          primaryEmail: customer.email || "",
          localBranches: "1",
          overseasBranches: "0",
          overseasCountries: "",
          country: options.country.find((o) => o.value === "Singapore"),
          address1: "456 Office Rd",
          address2: "",
          city: "Singapore",
          postalCode: "123456",
        });

        // Set mock beneficiaries for edit
        setBeneficiaries([
          {
            id: 1,
            isCollapsed: true,
            fullName: "Owner A",
            ownership: "60",
          },
          {
            id: 2,
            isCollapsed: true,
            fullName: "Owner B",
            ownership: "40",
          },
        ]);
      } else {
        // Reset form for create
        setFormData({
          phonePrefix:
            options.phonePrefix.find((p) => p.value === "+65") || null,
          // ... rest empty
        });
        setBeneficiaries([
          { id: 1, isCollapsed: false, fullName: "", ownership: "" },
        ]);
      }
    }
  }, [show, customer]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const formatOptionLabel = ({ label, flag, value }, { context }) => (
    <div className="d-flex align-items-center">
      {flag && (
        <img
          src={flag}
          alt=""
          style={{ width: "20px", marginRight: "8px", borderRadius: "2px" }}
        />
      )}
      <span style={{ fontSize: "13px" }}>
        {context === "value" ? value : label}
      </span>
    </div>
  );

  const totalOwnership = useMemo(() => {
    return beneficiaries.reduce(
      (sum, b) => sum + (parseFloat(b.ownership) || 0),
      0,
    );
  }, [beneficiaries]);

  const addBeneficiary = () => {
    setBeneficiaries([
      ...beneficiaries,
      {
        id: Date.now(),
        isCollapsed: false,
        fullName: "",
        ownership: "",
      },
    ]);
  };

  const toggleCollapse = (id) => {
    setBeneficiaries(
      beneficiaries.map((b) =>
        b.id === id ? { ...b, isCollapsed: !b.isCollapsed } : b,
      ),
    );
  };

  const removeBeneficiary = (id) => {
    if (beneficiaries.length > 1) {
      setBeneficiaries(beneficiaries.filter((b) => b.id !== id));
    }
  };

  const updateBeneficiary = (id, field, value) => {
    setBeneficiaries(
      beneficiaries.map((b) => (b.id === id ? { ...b, [field]: value } : b)),
    );
  };

  const [files, setFiles] = useState({});

  const handleFileChange = (key, event) => {
    const file = event.target.files[0];
    if (file) {
      setFiles((prev) => ({ ...prev, [key]: file }));
    }
  };

  const removeFile = (key, e) => {
    e.stopPropagation();
    setFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[key];
      return newFiles;
    });
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    Swal.fire({
      icon: "success",
      title: "Success",
      text: isEdit
        ? "Organization Customer updated successfully!"
        : "Organization Customer created successfully!",
      confirmButtonColor: "#00264D",
    }).then(() => {
      onHide();
    });
  };

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      size="xl"
      title={isEdit ? "Edit Organization Customer" : "Create New Organization Customer"}
      footer={
        <>
          <CommonButton variant="outline" onClick={onHide} className="me-2">
            Close
          </CommonButton>
          <CommonButton variant="primary" onClick={handleSubmit}>
            {isEdit ? "Save Changes" : "Create Customer"}
          </CommonButton>
        </>
      }
    >
      <p className="mb-4 text-muted fs-14">
        {isEdit ? "Update the organization customer information below." : "Fill in the organization customer information to create a new organization customer."}
      </p>
      <form onSubmit={handleSubmit}>
        {/* Customer Type Section */}
        <div className="mb-4">
          <CommonInput
            label="Customer Type"
            value="Organization"
            readOnly
            wrapClass="w-25"
          />
        </div>

        {/* Personal Information (Main Contact) */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">
            Personal Information (Main Contact)
          </h6>
        </div>
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <CommonInput
              label="Full Name"
              placeholder="Enter full name"
              value={formData.mainContactName}
              onChange={(e) => handleChange("mainContactName", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonDatePicker
              label="Date of Birth"
              value={formData.mainContactDob}
              onChange={(val) => handleChange("mainContactDob", val)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Email"
              placeholder="Enter email"
              value={formData.mainContactEmail}
              onChange={(e) => handleChange("mainContactEmail", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonSelect
              label="Nationality"
              placeholder="Select an option"
              options={options.nationality}
              formatOptionLabel={formatOptionLabel}
              value={formData.mainContactNationality}
              onChange={(val) => handleChange("mainContactNationality", val)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonSelect
              label="ID Type"
              placeholder="Select an option"
              options={options.idType}
              value={formData.mainContactIdType}
              onChange={(val) => handleChange("mainContactIdType", val)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="ID Number"
              placeholder="Enter ID number"
              value={formData.mainContactIdNumber}
              onChange={(e) => handleChange("mainContactIdNumber", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Job Title"
              placeholder="Enter job title"
              value={formData.mainContactJobTitle}
              onChange={(e) => handleChange("mainContactJobTitle", e.target.value)}
            />
          </Col>
          <Col md={6} className="mb-3">
            <CommonInput
              label="Residential Address"
              placeholder="Enter residential address"
              value={formData.residentialAddress}
              onChange={(e) => handleChange("residentialAddress", e.target.value)}
            />
          </Col>
        </Row>

        {/* Organization Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Organization Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={4} className="mb-3">
            <CommonInput
              label="Registered Name"
              placeholder="Enter registered name"
              value={formData.registeredName}
              onChange={(e) => handleChange("registeredName", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="UEN/Registration Number"
              placeholder="Enter UEN/Registration Number"
              value={formData.registrationNumber}
              onChange={(e) => handleChange("registrationNumber", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonSelect
              label="Type of Entity"
              options={options.entityType}
              placeholder="Select an option"
              value={formData.entityType}
              onChange={(val) => handleChange("entityType", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="Type of Entity (Specify if others)"
              placeholder="Select type of entity"
              value={formData.entityTypeOther}
              onChange={(e) => handleChange("entityTypeOther", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonDatePicker
              label="Date of Incorporation"
              value={formData.incorporationDate}
              onChange={(val) => handleChange("incorporationDate", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="Business Activity"
              placeholder="Enter business activity"
              value={formData.businessActivity}
              onChange={(e) => handleChange("businessActivity", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <label className="text-black fw-bold mb-1">Phone Number</label>
            <div className="d-flex">
              <div style={{ width: "130px", marginRight: "10px" }}>
                <CommonSelect
                  placeholder="+65"
                  options={options.phonePrefix}
                  value={formData.phonePrefix}
                  onChange={(val) => handleChange("phonePrefix", val)}
                  formatOptionLabel={formatOptionLabel}
                  styles={{
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0 4px",
                    }),
                  }}
                />
              </div>
              <div className="flex-grow-1">
                <CommonInput
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />
              </div>
            </div>
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label={
                <span>
                  Primary Email Address{" "}
                  <i className="fas fa-info-circle text-muted ms-1 fs-12"></i>
                </span>
              }
              placeholder="Enter primary email address"
              value={formData.primaryEmail}
              onChange={(e) => handleChange("primaryEmail", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="Number of Local Branches"
              placeholder="Enter number of local branches"
              value={formData.localBranches}
              onChange={(e) => handleChange("localBranches", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput
              label="Number of Overseas Branches"
              placeholder="Enter number of overseas branches"
              value={formData.overseasBranches}
              onChange={(e) => handleChange("overseasBranches", e.target.value)}
            />
          </Col>
          <Col md={8} className="mb-3">
            <CommonInput
              label="Countries Overseas Branches are Situated in"
              placeholder="Enter countries overseas branches are situated in"
              value={formData.overseasCountries}
              onChange={(e) => handleChange("overseasCountries", e.target.value)}
            />
          </Col>
        </Row>

        {/* Organization Address */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Organization Address</h6>
        </div>
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <CommonSelect
              label="Country"
              options={options.country}
              placeholder="Select an option"
              formatOptionLabel={formatOptionLabel}
              value={formData.country}
              onChange={(val) => handleChange("country", val)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Address Line 1"
              placeholder="Enter address line 1"
              value={formData.address1}
              onChange={(e) => handleChange("address1", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Address Line 2 (Optional)"
              placeholder="Enter address line 2"
              value={formData.address2}
              onChange={(e) => handleChange("address2", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="City"
              placeholder="Enter city"
              value={formData.city}
              onChange={(e) => handleChange("city", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Postal Code"
              placeholder="Enter postal code"
              value={formData.postalCode}
              onChange={(e) => handleChange("postalCode", e.target.value)}
            />
          </Col>
        </Row>

        {/* Beneficial Owners */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Beneficial Owners</h6>
        </div>
        <div className="mb-3 px-1">
          <span className="fw-bold fs-14">Total: {totalOwnership}%</span>
        </div>

        {beneficiaries.map((b, index) => (
          <div
            key={b.id}
            className="border rounded p-3 mb-3 bg-white shadow-sm"
            style={{ position: "relative" }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 fs-14">
                {b.fullName || `Beneficiary ${index + 1}`} ({b.ownership || 0}%)
              </h6>
              <div className="d-flex gap-2">
                {beneficiaries.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-light btn-sm d-flex align-items-center justify-content-center border"
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      backgroundColor: "#fff",
                    }}
                    onClick={() => removeBeneficiary(b.id)}
                  >
                    <i className="far fa-trash-alt text-danger"></i>
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-light btn-sm px-3 fw-bold border"
                  style={{
                    height: "36px",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    color: "#333",
                    fontSize: "13px",
                  }}
                  onClick={() => toggleCollapse(b.id)}
                >
                  {b.isCollapsed ? "Expand" : "Collapse"}
                </button>
              </div>
            </div>

            {!b.isCollapsed && (
              <Row>
                <Col md={3} className="mb-3">
                  <CommonInput
                    label="Full Name"
                    placeholder="Enter full name"
                    value={b.fullName}
                    onChange={(e) =>
                      updateBeneficiary(b.id, "fullName", e.target.value)
                    }
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <CommonDatePicker label="Date of Birth" />
                </Col>
                <Col md={3} className="mb-3">
                  <CommonSelect
                    label="Nationality"
                    options={options.nationality}
                    placeholder="Select an option"
                    formatOptionLabel={formatOptionLabel}
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <CommonSelect
                    label="ID Type"
                    options={options.idType}
                    placeholder="Select an option"
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <CommonInput
                    label="ID Number"
                    placeholder="Enter ID number"
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <CommonInput
                    label="Residential Address"
                    placeholder="Enter residential address"
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <CommonInput
                    label="Ownership Interest (%)"
                    placeholder="Enter ownership interest (%)"
                    value={b.ownership}
                    onChange={(e) =>
                      updateBeneficiary(b.id, "ownership", e.target.value)
                    }
                  />
                </Col>
              </Row>
            )}
          </div>
        ))}

        <div className="mb-4">
          <CommonButton
            onClick={addBeneficiary}
            className="p-0 d-flex align-items-center justify-content-center"
            style={{ width: "50px", height: "50px", minWidth: "50px" }}
          >
            <i className="fas fa-plus fs-20"></i>
          </CommonButton>
        </div>

        {/* Document Upload */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Document Upload</h6>
        </div>
        <Row className="mb-4">
          {[
            {
              key: "signed_pdf",
              label: "Signed PDF Form",
              desc: "Upload document",
              small: ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB)",
            },
            {
              key: "moa",
              label: "Memorandum & Article of Association",
              desc: "Upload document",
              small: ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB)",
            },
            {
              key: "acra_biz",
              label: "ACRA Biz Profile",
              desc: "Upload document",
              small: ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB)",
            },
            {
              key: "directors_id",
              label: "Directors & Owners ID",
              desc: "Upload documents",
              small:
                ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB each, up to 10 files)",
            },
            {
              key: "authorised_persons_id",
              label: "Authorised Persons ID",
              desc: "Upload documents",
              small:
                ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB each, up to 10 files)",
            },
            {
              key: "org_chart",
              label: "Organization Chart",
              desc: "Upload documents",
              small:
                ".jpg, .jpeg, .png, .heic, .heif, .pdf, .docx (Max 5MB each, up to 10 files)",
            },
          ].map((box, i) => (
            <Col md={4} key={i} className="mb-4 d-flex flex-column">
              <label className="text-black fw-bold mb-2 fs-13 flex-grow-0 ps-1">
                {box.label}
              </label>
              <div
                className="upload-box-premium d-flex p-3 align-items-center gap-3 w-100 flex-grow-1"
                style={{
                  border: files[box.key]
                    ? "1.5px solid #003b95"
                    : "1.5px dashed #cfd9e0",
                  borderRadius: "12px",
                  cursor: "pointer",
                  height: "100px",
                  backgroundColor: files[box.key] ? "#f0f7ff" : "#ffffff",
                  transition: "all 0.25s ease-in-out",
                  position: "relative",
                  overflow: "hidden",
                }}
                onClick={() =>
                  document.getElementById(`file-input-${box.key}`).click()
                }
              >
                <input
                  type="file"
                  id={`file-input-${box.key}`}
                  className="d-none"
                  onChange={(e) => handleFileChange(box.key, e)}
                  accept="image/*,.pdf,.doc,.docx"
                />
                <div
                  className="icon-container d-flex align-items-center justify-content-center"
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "10px",
                    backgroundColor: files[box.key] ? "#003b95" : "#f4f7fa",
                    color: files[box.key] ? "#ffffff" : "#003b95",
                    fontSize: "20px",
                    flexShrink: 0,
                  }}
                >
                  <i
                    className={`fas ${files[box.key] ? "fa-check" : "fa-cloud-upload-alt"}`}
                  ></i>
                </div>
                <div className="flex-grow-1 overflow-hidden text-start">
                  <p
                    className="mb-1 fw-bold text-dark fs-12 text-truncate"
                    style={{ letterSpacing: "0.2px" }}
                  >
                    {files[box.key] ? files[box.key].name : box.desc}
                  </p>
                  <div className="d-flex align-items-center text-muted">
                    <span className=" opacity-75" style={{ fontSize: "10px" }}>
                      {files[box.key]
                        ? `${(files[box.key].size / 1024).toFixed(1)} KB`
                        : box.small}
                    </span>
                  </div>
                </div>
                {files[box.key] && (
                  <div
                    className="position-absolute"
                    style={{ top: "10px", right: "10px" }}
                    onClick={(e) => removeFile(box.key, e)}
                  >
                    <i className="fas fa-times-circle text-danger fs-14"></i>
                  </div>
                )}
              </div>
            </Col>
          ))}
        </Row>
      </form>
    </CommonModal>
  );
};

export default OrganizationCustomerModal;
